import React, {Fragment, memo, useRef, useEffect} from 'react';
import {FormattedDate, FormattedTime, FormattedMessage} from 'react-intl';

import IntlStringHtml from '../intl-string-html';
import {isDateEq, renderWeekdayBounds} from '../../utils/events';
import Link from '../link';

import Columns from '../columns';

import style from './event.module.scss';
import HidableBox from '../hidable-box';
import {styleMods} from '../../utils';
import Block from '../block';

const EventDates = memo(
	/**
	 * @param {{event: import("../../utils/events").Event}} props
	 */
	function EventDates({event}) {
		return (
			<ol className={style.dates}>
				{event.dates.map((date, i) => {
					const onlyOneDay = isDateEq(date.from, date.to);

					const dateFrag = (() => {
						if (onlyOneDay) {
							return (
								<FormattedDate
									value={date.from.date}
									timeZone="Europe/Berlin"
									weekday="short"
									month="short"
									day="2-digit"
								/>
							);
						} else {
							return (
								<Fragment>
									<FormattedDate
										value={date.from.date}
										timeZone="Europe/Berlin"
										month="short"
										day="2-digit"
									/>
									{' - '}
									<FormattedDate
										value={date.to.date}
										timeZone="Europe/Berlin"
										month="short"
										day="2-digit"
									/>
								</Fragment>
							);
						}
					})();

					const timeFrag = date.from.hasTime && (
						<FormattedMessage
							id="time.oclock"
							values={{time: (
								<Fragment>
									<FormattedTime
										value={date.from.date}
										timeZone="Europe/Berlin"
										hour="2-digit"
										minute="2-digit"
									/>
									{date.to.hasTime && (
										<Fragment>
											{' - '}
											<FormattedTime
												value={date.to.date}
												timeZone="Europe/Berlin"
												hour="2-digit"
												minute="2-digit"
											/>
										</Fragment>
									)}
								</Fragment>
							)}}
						/>
					);

					return (
						<li key={i} className={style.date}>

							{(() => {
								if (onlyOneDay && !date.weekdays && !date.to.hasTime) {
									return (
										<Fragment>
											{dateFrag}
											{timeFrag && (
												<Fragment>
													{' | '}
													{timeFrag}
												</Fragment>
											)}
										</Fragment>
									);
								} else {
									return (
										<Fragment>
											{dateFrag}

											<br />

											{!onlyOneDay && date.weekdays && (
												<Fragment>
													{renderWeekdayBounds(date.weekdays)}

													{date.to.hasTime && (
														<br />
													)}
												</Fragment>
											)}
											{timeFrag}
										</Fragment>
									);
								}
							})()}
						</li>
					);
				})}
			</ol>
		);
	}
);

const EventDescriptionBlock = memo(
	/**
	 * @param {{
	 *   event: import("../../utils/events").Event,
	 * }} props
	 */
	function EventDescriptionBlock({event}) {
		return (
			<Fragment>
				<p className={style.description}>
					<IntlStringHtml {...event.description} />
				</p>

				<dl className={style.metaList}>
					{(event.location.title || event.location.streetHouseNumber) && (
						<div className={style.metaListEntry}>
							<dt className={style.metaListTerm}>
								Veranstaltungsort
							</dt>
							<dd className={style.metaListDesc}>
								{event.location.title && (
									<Fragment>
										{event.location.title}

										{event.location.streetHouseNumber && (
											<br />
										)}
									</Fragment>
								)}

								{event.location.streetHouseNumber && (
									<Fragment>
										{event.location.streetHouseNumber[0]}
										{event.location.streetHouseNumber[1]}
									</Fragment>
								)}
							</dd>
						</div>
					)}

					{event.organizer && (
						<div className={style.metaListEntry}>
							<dt className={style.metaListTerm}>
								Veranstalter
							</dt>
							<dd className={style.metaListDesc}>
								{event.organizer}
							</dd>
						</div>
					)}


					{event.phone && (
						<div className={style.metaListEntry}>
							<dt className={style.metaListTerm}>
								Tel.:
							</dt>
							<dd className={style.metaListDesc}>
								{event.phone}
							</dd>
						</div>
					)}


					{event.webUrl && (
						<div className={style.metaListEntry}>
							<dt className={style.metaListTerm}>
								Web
							</dt>
							<dd className={style.metaListDesc}>
								<Link
									href={event.webUrl}
									addExternalIcon={true}
								>
									{event.webUrl}
								</Link>
							</dd>
						</div>
					)}

					{(event.registrationRequired === "1") && (
						<div className={style.metaListEntry}>
							<dt className={style.metaListTerm}>
								Anmeldung erforderlich
							</dt>
							<dd className={style.metaListDesc}>
								ja
							</dd>
						</div>
					)}

					{event.registrationEmail && (
						<div className={style.metaListEntry}>
							<dt className={style.metaListTerm}>
								Anmeldung online
							</dt>
							<dd className={style.metaListDesc}>
								{event.registrationEmail}
							</dd>
						</div>
					)}

					{event.admission && (
						<div className={style.metaListEntry}>
							<dt className={style.metaListTerm}>
								Eintritt
							</dt>
							<dd className={style.metaListDesc}>
								{event.admission}
							</dd>
						</div>
					)}

					{event.additionalFees && (
						<div className={style.metaListEntry}>
							<dt className={style.metaListTerm}>
								Kostenbeiträge
							</dt>
							<dd className={style.metaListDesc}>
								{event.additionalFees}
							</dd>
						</div>
					)}

				</dl>
			</Fragment>
		);
	}
);

const EventBody = memo(
	/**
	 * @param {{
	 *   event: import("../../utils/events").Event,
	 *   visible: boolean,
	 * }} props
	 */
	function EventBody({event, visible}) {
		return (
			<div className={style.body}>
				<Columns>
					<EventDates event={event} />

					<EventDescriptionBlock event={event} />

					<Fragment>
						{visible && event.image && (
							<figure title={event.image.description}>
								<img
									className={style.image}
									src={event.image.url}
									alt={event.image.description || ''}
									loading="lazy"
								/>

								{event.image.description && (
									<figcaption className={style.imageCaption}>
										{event.image.description}
									</figcaption>
								)}
							</figure>
						)}
					</Fragment>
				</Columns>
			</div>
		);
	}
);

/**
 * passing in `toggleVisible` makes the `Event` "hidable"
 */
const Event = memo(
	/**
	 * @param {{
	 *   event: import("../../utils/events").Event,
	 *   visible: boolean,
	 *   hashOpenedEventId: null | string,
	 *   dataVersion: number,
	 *   toggleVisible?: () => void,
	 * }} props
	 * @returns {React.ReactElement} ...
	 */
	function Event({
		event,
		visible,
		hashOpenedEventId,
		dataVersion,
		toggleVisible,
	}) {
		/** @type {React.MutableRefObject<HTMLElement | null>} */
		const anchorRef = useRef(null);

		useEffect(
			() => {
				if (event.id === hashOpenedEventId) {
					setTimeout(
						() => {
							if (anchorRef.current) {
								anchorRef.current.scrollIntoView({
									behavior: 'smooth',
									block: 'start',
									inline: 'nearest',
								});
							}
						},
						500,
					);
				}
			},
			[hashOpenedEventId, dataVersion, event.id],
		);

		const anchor = (
			<span
				id={event.id}
				className={style.anchor}
				ref={anchorRef}
			/>
		);

		const title = (
			<h1 className={style.title}>
				{event.title}
			</h1>
		);

		if (toggleVisible !== undefined) {
			return (
				<Block>
					<article className={styleMods(style, 'base', 'hidable')}>
						{anchor}

						<HidableBox
							visible={visible}
							toggleVisible={toggleVisible}
							header={title}
						>
							<EventBody event={event} visible={visible} />
						</HidableBox>
					</article>
				</Block>
			);
		} else {
			return (
				<article className={style.base}>
					{anchor}

					<a href={`#${event.id}`} className={style.titleLink}>
						{title}
					</a>

					<EventBody event={event} visible={visible} />
				</article>
			);
		}
	}
);
export default Event;
